import type { ApprovalRow } from 'src/pages/admin/services/vaultService';
import { TURNKEY_ACTIVITY_URL } from './constants';

/** The engine's `WorkOrderLane`s and `WorkOrderStatus`es (xbit-vault-engine internal/domain), the words of each are in i18n `signers.lane` / `signers.what`. */
export const SIGNER_LANES = [
  'VAULT_TO_COLD',
  'COLD_TO_WARM',
  'WARM_TO_HOT',
  'HOT_TO_USER_AUTO',
  'HOT_TO_WARM',
  'WARM_TO_USER',
] as const;
export const SIGNER_STATUSES = [
  'DRAFT',
  'AWAITING_QUORUM',
  'AWAITING_FLOAT',
  'SIGNED',
  'BROADCAST',
  'CONFIRMED',
  'FAILED',
  'EXPIRED',
  'UNRESOLVED',
] as const;

const isIn = <T extends string>(list: readonly T[], value: string): value is T =>
  (list as readonly string[]).includes(value);

/** A lane or status the console has no words for reads as `unknown`: a neutral sentence, never the raw enum in the main text. */
export const laneKey = (lane: string): string => (isIn(SIGNER_LANES, lane) ? lane : 'unknown');
export const statusKey = (status: string): string =>
  isIn(SIGNER_STATUSES, status) ? status : 'unknown';

export const isKnownStatus = (status: string): boolean => isIn(SIGNER_STATUSES, status);

/**
 * The engine's reason is free text (a transition's `reason`, up to 255 characters), so it is a sentence only when it reads like
 * one: a single token ("AWAITING_VENUE") is a code and stays under Technical details.
 */
export const readableReason = (reason: string | null): string | null => {
  const text = reason?.trim();
  return text && /\s/.test(text) ? text : null;
};

/** The deep link into Turnkey's activity, or null while the engine has not recorded an activity id. */
export const turnkeyUrl = (activityId: string | null): string | null =>
  activityId ? `${TURNKEY_ACTIVITY_URL}/${encodeURIComponent(activityId)}` : null;

/** `waitingSince` is nullable: an order that never recorded one waits from its creation. */
export const waitingSinceOf = (row: Pick<ApprovalRow, 'waitingSince' | 'createdAt'>): string =>
  row.waitingSince ?? row.createdAt;
